import { exec } from "js-exec";
import { JSHINT } from "jshint";
import {
  Debugger,
  delayCall,
  destroy,
  Device,
  Haptics,
  HttpRequest,
  SURL,
  KeyboardInput,
  MouseButton,
  MouseInput,
  ObjectPool,
  Random,
  runAllCancellationHandlers,
  Singleton,
  spawn,
  WebBrowser,
  Canvas,
} from "../api/ApiContainer.js";
import { DEFAULT_CONST_AND_MACROS, SAFE_GLOBALS } from "./DefaultContext.js";
import { Logger } from "./Logger.js";
import { ProxyUrl } from "./ProxyUrl.js";
import { ScriptingPreprocessor } from "./ScriptingPreprocessor.js";
import { SharedContext } from "./SharedContext.js";
import { SlotContext } from "./SlotContext.js";

/**
 *
 * @param {*} fn
 * @returns
 */
const isFunction = (fn) => {
  return fn instanceof Function;
};

/**
 *
 */
const applyFrameState = (handler, state, once) => {
  let recurrent = false;
  if ((handler?.state & state) == state) {
    recurrent = true;
    if (once) {
      handler.state &= ~state;
    }
  }
  return recurrent;
};

/**
 *
 * @param {*} handler
 * @param {*} action
 * @param {*} delta
 * @param {*} time
 */
const safeAsyncCall = async (handler, action, delta, time) => {
  try {
    if (isFunction(action)) {
      await action(delta, time);
    }
  } catch (e) {
    Logger.error(`${handler.node.name}::${action.name}() -> ${e}`);
    handler.state = DisposeState;
  }
};

/**
 *
 */
class LifecycleHandler {
  state = 0;
  node = null;
  startup = () => {};
  update = (delta, time) => {};
  dispose = () => {};
}

/**
 *  States
 */
const CleanupState = 0x01;
const StartupState = 0x02;
const UpdateState = 0x04;
const DisposeState = 0x08;

/**
 *  ScriptingRuntime
 */
export class ScriptingRuntime {
  /**
   *
   */
  constructor(options = { devMode: false, proxyUrl: null }) {
    this._options = options;
    this._lifecycleHandlers = [];

    if (this._options.proxyUrl) {
      ProxyUrl.setProxy(this._options.proxyUrl);
    }

    const { macros, constants } = this.setupEnvironmentVariables(
      DEFAULT_CONST_AND_MACROS,
      options
    );

    this._preprocessor = new ScriptingPreprocessor({
      optimize: this._options.devMode == false,
      macros,
      constants,
    });

    this._executionIndexInvalidated = true;
  }

  /**
   *
   * @param {*} options
   */
  setupEnvironmentVariables(values = { macros: {}, constants: {} }, options) {
    if (options.devMode == true) {
      values.constants.DEBUG = true;
      values.constants.EDITOR = true;
    }

    if (Device.isMobile()) {
      values.constants.IS_MOBILE = true;
    }

    const currentOS = Device.operatingSystem();
    switch (currentOS) {
      case "Windows":
        values.constants.PLATFORM_WINDOWS = true;
        break;
      case "macOS":
        values.constants.PLATFORM_MACOS = true;
        break;
      case "Linux":
        values.constants.PLATFORM_LINUX = true;
        break;
      case "Android":
        values.constants.PLATFORM_ANDROID = true;
        break;
      case "iOS":
        values.constants.PLATFORM_IOS = true;
        break;
    }
    return values;
  }

  /**
   *
   * @param {*} node
   * @param {*} context
   * @returns
   */
  defineAPIGlobals(node, context) {
    return {
      self: node,
      spawn: spawn(node, this, context),
      destroy: destroy(node, this),
      delayCall,
      console: Logger,
      Logger,
      Canvas,
      HttpRequest,
      SURL,
      Device,
      WebBrowser,
      Debugger,
      Haptics,
      KeyboardInput,
      MouseInput,
      MouseButton,
      Random,
      ObjectPool,
      Singleton,
      SharedContext,
      SlotContext,
    };
  }

  /**
   * @typedef {Object} BehaviourNodeNode
   * @property {string} id
   * @property {string} code
   * @property {Number} executionIndex
   */

  /**
   * Execute javascript code using lifecycle hooks.
   * @param {BehaviourNodeNode} node
   * @param {*} context
   */
  async registerBehaviourNode(node, context) {
    if (context?.loggerTransport) {
      Logger.useTransport(context.loggerTransport);
    }

    const handler = new LifecycleHandler();
    const taskContext = {
      // ---> Context definition
      ...context,
      ...SAFE_GLOBALS,
      // ----> Adding lifecycle control
      lifecycle: (startup, update, dispose) => {
        if (startup) handler.startup = startup;
        if (update) handler.update = update;
        if (dispose) handler.dispose = dispose;
      },
      // ----> Defines scripting API
      ...this.defineAPIGlobals(node, context),
    };

    const { devMode } = this._options;
    const { code, id } = node;
    const { output } = await this._preprocessor.run(code, id);
    if (devMode) {
      this.runStaticCodeAnalysis(output, taskContext);
    }

    const taskRunner = exec(output);
    taskRunner(taskContext);

    handler.node = node;
    handler.state = StartupState | UpdateState;

    this._lifecycleHandlers.push(handler);
    this._executionIndexInvalidated = true;

    return handler;
  }
  /**
   * Sort nodes
   * @param {*} nodes
   * @returns
   */
  prepareBehaviourNodes(nodes) {
    nodes.sort((a, b) => a.executionIndex - b.executionIndex);
    return nodes;
  }

  /**
   * Compile javascript code using lifecycle hooks.
   * @param {*} node
   * @param {*} context
   */
  async compile(node, context) {
    const taskContext = {
      // ---> Context definition
      ...context,
      ...SAFE_GLOBALS,
      // ----> Adding lifecycle control
      lifecycle: (startup, update, dispose) => {},
      // ----> Defines scripting API
      ...this.defineAPIGlobals(node, context),
    };

    const { code, id } = node;
    const { output } = await this._preprocessor.run(code, id);
    this.runStaticCodeAnalysis(output, taskContext);
  }

  /**
   * Detects syntax errors, semantic errors, and potential bugs.
   * @param {*} code The input JavaScript source code.
   * @param {*} context Global definitions
   */
  runStaticCodeAnalysis(code, context) {
    const globals = {};
    Object.keys(context).forEach((key) => {
      globals[key] = true;
    });

    const jshintOptions = {
      esversion: 11,
      strict: false,
      undef: true,
      unused: false,
      latedef: true,
      varstmt: true,
      nonew: true,
      curly: true,
      leanswitch: true,
      freeze: true,
      loopfunc: true,
      maxerr: 3,
      globals,
    };

    const success = JSHINT(code, jshintOptions);
    if (!success) {
      const { errors } = JSHINT.data();
      const syntaxErrors = errors.map((e) => ({
        evidence: e.evidence,
        line: e.line,
        message: e.reason,
      }));
      syntaxErrors.sort((a, b) => a.line - b.line);
      const [first] = syntaxErrors;
      const err = new SyntaxError(`${first.message} (line: ${first.line})`);
      err.line = first.line;
      err.evidence = first.evidence?.trimLeft();
      throw err;
    }
  }

  /**
   *  Removes specific node
   */
  unregisterBehaviourNode(node) {
    const handler = this._lifecycleHandlers.find((h) => h.node.id == node.id);
    if (handler) {
      handler.state = DisposeState;
    }
  }

  /**
   *  Removes all registered nodes
   */
  unregisterAllBehaviourNodes() {
    runAllCancellationHandlers();

    for (const handler of this._lifecycleHandlers) {
      handler.state = DisposeState;
    }

    this.tick(0, 1);
    this.reset();
  }

  /**
   * Resets the scripting runtime.
   */
  reset() {
    SharedContext.clear();
    this._preprocessor.resetSession();
    this._lifecycleHandlers = [];
    this._executionIndexInvalidated = true;
  }

  /**
   * Executes the life cycle of the behaviors.
   * @param {*} delta
   * @param {*} time
   */
  tick(delta, time) {
    if (this._executionIndexInvalidated) {
      this._lifecycleHandlers.sort(
        (a, b) => a.node.executionIndex - b.node.executionIndex
      );
      this._executionIndexInvalidated = false;
    }

    for (const hdlr of this._lifecycleHandlers) {
      // <startup> state ⮐
      if (applyFrameState(hdlr, StartupState, true)) {
        safeAsyncCall(hdlr, hdlr.startup);
        continue;
      }

      // <update> state ⮐
      if (applyFrameState(hdlr, UpdateState, false)) {
        safeAsyncCall(hdlr, hdlr.update, delta, time);
      }

      // <dispose> state ⮐
      if (applyFrameState(hdlr, DisposeState, true)) {
        safeAsyncCall(hdlr, hdlr.dispose);
        hdlr.state = CleanupState;
        this._lifecycleHandlers = this._lifecycleHandlers.filter(
          (h) => h.state != CleanupState
        );
      }
    }
  }
}
